import type {
	CampaignEngagementTerms,
	EngagementTermsCustomizationField,
	SaveCampaignEngagementTerms,
} from "@/api/engagementTerms";
import { formatBudgetValue } from "@/shared/utils/budget";

export type DeliverableRow = {
	id: number;
	name: string;
	dueDate: string;
};

export type DeliverableItem = {
	id: number;
	name: string;
	deliverableTypes: string[];
	dueDate: string;
	liveDate: string;
	items: DeliverableRow[];
};

export type DeliverableDraft = Pick<DeliverableItem, "name" | "deliverableTypes" | "dueDate">;

export type CetSnapshot = {
	startDate: string;
	endDate: string;
	currency: string;
	paymentTerms: string;
	amount: string;
	exclusivityEnabled: boolean;
	exclusivityPeriod: string;
	exclusivityCategory: string;
	brands: string[];
	deliverables: DeliverableItem[];
};

export const cloneDeliverables = (deliverables: DeliverableItem[]): DeliverableItem[] =>
	deliverables.map((deliverable) => ({
		...deliverable,
		deliverableTypes: [...deliverable.deliverableTypes],
		items: deliverable.items.map((item) => ({ ...item })),
	}));

export const cloneSnapshot = (snapshot: CetSnapshot): CetSnapshot => ({
	...snapshot,
	brands: [...snapshot.brands],
	deliverables: cloneDeliverables(snapshot.deliverables),
});

export const createEmptySnapshot = (): CetSnapshot => ({
	startDate: "",
	endDate: "",
	currency: "",
	paymentTerms: "",
	amount: "",
	exclusivityEnabled: false,
	exclusivityPeriod: "",
	exclusivityCategory: "",
	brands: [],
	deliverables: [],
});

export const snapshotFromResponse = (terms: CampaignEngagementTerms): CetSnapshot => ({
	startDate: terms.effectiveStart,
	endDate: terms.effectiveEnd,
	currency: terms.creatorFeeCurrency,
	paymentTerms: terms.paymentTerms,
	amount: terms.creatorFeeAmount == null ? "" : formatBudgetValue(terms.creatorFeeAmount),
	exclusivityEnabled: terms.exclusivityEnabled,
	exclusivityPeriod: terms.exclusivityPeriod || "",
	exclusivityCategory: terms.exclusivityCategory || "",
	brands: [...terms.exclusivityBrands],
	deliverables: terms.deliverables.map((deliverable, deliverableIndex) => ({
		id: deliverableIndex + 1,
		name: deliverable.name,
		deliverableTypes: [...deliverable.deliverableTypes],
		dueDate: deliverable.dueDate,
		liveDate:
			deliverableIndex === terms.deliverables.length - 1 ? terms.liveDate || deliverable.liveDate || "" : "",
		items: deliverable.items.map((item, itemIndex) => ({
			id: deliverableIndex * 1000 + itemIndex + 1,
			name: item.name,
			dueDate: item.dueDate,
		})),
	})),
});

export const snapshotToPayload = (
	snapshot: CetSnapshot,
	customizedFields: EngagementTermsCustomizationField[] = [],
): SaveCampaignEngagementTerms => ({
	effectiveStart: snapshot.startDate,
	effectiveEnd: snapshot.endDate,
	liveDate: snapshot.deliverables.at(-1)?.liveDate || null,
	paymentTerms: snapshot.paymentTerms,
	creatorFeeAmount: snapshot.amount === "" ? null : Number(snapshot.amount.replace(/,/g, "")),
	creatorFeeCurrency: snapshot.currency,
	exclusivityEnabled: snapshot.exclusivityEnabled,
	exclusivityPeriod: snapshot.exclusivityEnabled ? snapshot.exclusivityPeriod : null,
	exclusivityCategory: snapshot.exclusivityEnabled ? snapshot.exclusivityCategory : null,
	exclusivityBrands: snapshot.exclusivityEnabled ? snapshot.brands : [],
	deliverables: snapshot.deliverables.map((deliverable) => ({
		name: deliverable.name,
		deliverableTypes: [...deliverable.deliverableTypes],
		dueDate: deliverable.dueDate,
		liveDate: null,
		items: deliverable.items.map((item) => ({ name: item.name, dueDate: item.dueDate })),
	})),
	customizedFields,
});

const areStringListsEqual = (left: string[], right: string[]): boolean =>
	left.length === right.length && left.every((value, index) => value === right[index]);

export const areDeliverablesEqual = (left: DeliverableItem[], right: DeliverableItem[]): boolean =>
	left.length === right.length &&
	left.every((deliverable, deliverableIndex) => {
		const other = right[deliverableIndex];
		return (
			!!other &&
			deliverable.name === other.name &&
			deliverable.dueDate === other.dueDate &&
			deliverable.liveDate === other.liveDate &&
			areStringListsEqual(deliverable.deliverableTypes, other.deliverableTypes) &&
			deliverable.items.length === other.items.length &&
			deliverable.items.every(
				(item, itemIndex) =>
					item.name === other.items[itemIndex]?.name && item.dueDate === other.items[itemIndex]?.dueDate,
			)
		);
	});

export const areSnapshotsEqual = (left: CetSnapshot, right: CetSnapshot): boolean =>
	left.startDate === right.startDate &&
	left.endDate === right.endDate &&
	left.currency === right.currency &&
	left.paymentTerms === right.paymentTerms &&
	left.amount === right.amount &&
	left.exclusivityEnabled === right.exclusivityEnabled &&
	left.exclusivityPeriod === right.exclusivityPeriod &&
	left.exclusivityCategory === right.exclusivityCategory &&
	areStringListsEqual(left.brands, right.brands) &&
	areDeliverablesEqual(left.deliverables, right.deliverables);

export const getCustomizedFields = (
	snapshot: CetSnapshot,
	market: CetSnapshot,
): EngagementTermsCustomizationField[] => {
	const customized: EngagementTermsCustomizationField[] = [];
	if (snapshot.startDate !== market.startDate || snapshot.endDate !== market.endDate)
		customized.push("effectiveDates");
	if (snapshot.currency !== market.currency) customized.push("currency");
	if (snapshot.paymentTerms !== market.paymentTerms) customized.push("paymentTerms");
	if (!areDeliverablesEqual(snapshot.deliverables, market.deliverables)) customized.push("deliverables");
	if (
		snapshot.exclusivityEnabled !== market.exclusivityEnabled ||
		snapshot.exclusivityPeriod !== market.exclusivityPeriod ||
		snapshot.exclusivityCategory !== market.exclusivityCategory ||
		!areStringListsEqual(snapshot.brands, market.brands)
	) {
		customized.push("exclusivity");
	}
	return customized;
};

export const itemErrorKey = (deliverableId: number, itemId: number, field: "name" | "dueDate") =>
	`${deliverableId}:${itemId}:${field}`;

export const chronologyErrorKey = (deliverableId: number, itemId: number) => `${deliverableId}:${itemId}:dueDate`;

export const getChronologyErrors = (deliverables: DeliverableItem[]): Set<string> => {
	const errors = new Set<string>();
	deliverables.forEach((deliverable) => {
		for (let index = 1; index < deliverable.items.length; index += 1) {
			const previous = deliverable.items[index - 1];
			const current = deliverable.items[index];
			if (!previous.dueDate || !current.dueDate) continue;
			if (current.dueDate < previous.dueDate) {
				errors.add(chronologyErrorKey(deliverable.id, previous.id));
				errors.add(chronologyErrorKey(deliverable.id, current.id));
			}
		}
	});
	return errors;
};
